import React, { useEffect, useState, useCallback } from 'react';
import { devLog, devWarn, devError } from '../../../utils/devLogger';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation, Autoplay } from 'swiper/modules';
import { useLang } from '../../../context/LangContext';
import { useUserAuth } from '../../../context/UserAuthContext';
import ImageView from '../../../components/ImageView';
import ResponsiveView from '../../../layout/ResponsiveView';
import FavoriteIcon from '../../../components/common/FavoriteIcon';
import AuctionHouse from '../../../components/common/AuctionHouse';
import { getRecommendedLots } from '../../../lib/api/user/userApi';
import { FavoriteManager } from '../../../hooks/useFavoriteManager';

const SectionContainer = styled.div`
  width: 100%;
  overflow: hidden;
`;

// 모바일용 타이틀 컨테이너 (왼쪽 정렬)
const MobileTitleContainer = styled.div`
  text-align: left;
  margin-bottom: 30px;
  margin-top: 30px;
`;

// 데스크톱용 타이틀 컨테이너 (중앙 정렬)
const DesktopTitleContainer = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  color: #000000;
  margin: 0 0 8px 0;
  font-weight: bold;
`;

const SectionSubtitle = styled.p`
  font-size: 14px;
  color: #666666;
  margin: 0;
  line-height: 1.4;
`;

// 모바일용 슬라이드 카드 (세로 배치)
const MobileSlideCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: visible;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  width: 100%;
  max-width: 100%;

  &:hover {
    transform: translateY(-4px);
  }
`;

// 데스크톱용 슬라이드 카드 (가로 배치)
const DesktopSlideCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: visible;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  width: 100%;
  max-width: 100%;
  display: flex;
  gap: 40px;
  align-items: stretch;
  min-height: 400px;

  &:hover {
    transform: translateY(-4px);
  }
`;

// 데스크톱용 이미지 컨테이너 (flex: 1)
const DesktopCardImageContainer = styled.div`
  flex: 1;
  position: relative;
  overflow: visible;
  border-radius: 8px;
  padding: 16px;
  box-sizing: border-box;
  max-width: 500px;
  max-height: 500px;
  width: 500px;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// 데스크톱용 콘텐츠 (flex: 1, space-between 구조)
const DesktopCardContent = styled.div`
  flex: 1;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

// 데스크톱용 텍스트 영역 (위쪽 정렬)
const DesktopTextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RecommendationGrid = styled.div`
  .swiper {
    padding: 0 0 40px 0;
    overflow: visible;
  }
  
  .swiper-wrapper {
    overflow: visible;
  }
  
  .swiper-slide {
    height: auto;
    overflow: visible;
  }
  
  .swiper-pagination-bullet {
    background: #ddd;
    opacity: 1;
  }
  
  .swiper-pagination-bullet-active {
    background: #007bff;
  }
`;

// 데스크톱용 네비게이션 스타일 추가
const DesktopRecommendationGrid = styled.div`
  position: relative;
  
  .swiper {
    padding: 0 40px 40px 40px; /* 네비게이션 버튼 공간 확보 */
    overflow: visible;
  }
  
  .swiper-wrapper {
    overflow: visible;
  }
  
  .swiper-slide {
    height: auto;
    overflow: visible;
  }
  
  .swiper-pagination-bullet {
    background: #ddd;
    opacity: 1;
  }
  
  .swiper-pagination-bullet-active {
    background: #007bff;
  }

  /* 네비게이션 버튼 스타일 */
  .swiper-button-next,
  .swiper-button-prev {
    width: 44px;
    height: 44px;
    margin-top: -22px;
    background: white;
    border: 2px solid #e5e5e5;
    border-radius: 50%;
    color: #666;
    font-size: 16px;
    font-weight: bold;
    transition: all 0.2s ease;
    
    &:after {
      font-size: 16px;
      font-weight: bold;
    }
    
    &:hover {
      background: #f8f9fa;
      border-color: #007bff;
      color: #007bff;
      transform: scale(1.05);
    }
    
    &.swiper-button-disabled {
      opacity: 0.4;
      cursor: not-allowed;
      
      &:hover {
        background: white;
        border-color: #e5e5e5;
        color: #666;
        transform: none;
      }
    }
  }
  
  .swiper-button-prev {
    left: 10px;
  }
  
  .swiper-button-next {
    right: 10px;
  }
`;

const CardImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1; /* 정사각형 비율 유지 */
  position: relative;
  overflow: visible;
  border-radius: 8px 8px 0 0;
  padding: 16px; /* 내부 패딩 추가 */
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// RecommendationIcon styled component - 공통 FavoriteIcon으로 대체됨

const CardContent = styled.div`
  padding: 16px;
  text-align: left;
`;

const AuctionTitle = styled.div`
  font-size: 20px;
  color: #000;
    font-weight: 600;
  margin-bottom: 8px;
  text-align: left;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
`;

const LotName = styled.h3`
  font-size: 16px;
  color: #000;
  margin: 0;
  font-weight: 400;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: left;
`;

// API 함수로 데이터를 가져오는 컴포넌트로 변경

// 이미지 URL 처리 함수
const getProfileImageSrc = (profileImageUrl?: string | null) => {
  if (!profileImageUrl || profileImageUrl.trim() === "") {
    return "/common/nodata.svg";
  }
  
  // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
  if (profileImageUrl.startsWith("http") || profileImageUrl.startsWith("blob:")) {
    return profileImageUrl;
  }
  
  // 절대 경로인 경우 (/로 시작)
  if (profileImageUrl.startsWith("/")) {
    return `${process.env.REACT_APP_API_HOST}${profileImageUrl}`;
  }
  
  // 단순 파일명인 경우 (서버에서 받은 파일명)
  return `${process.env.REACT_APP_API_HOST}/${profileImageUrl}`;
};

// 임시로 언어 상태를 관리 (나중에 LangProvider로 교체 예정)
const getTexts = (language: 'ko' | 'en') => {
  if (language === 'en') {
    return {
      title: 'The Bidspace Recommended LOT',
      subtitle: 'Auctions recommended by BidSpace',
      viewArtwork: 'View Artwork',
      startPrice: 'Starting Price'
    };
  }
  
  return {
    title: 'The BIdspace 추천 LOT',
    subtitle: 'The Bidspace에서 추천드리는 경매에요',
    viewArtwork: '작품보러가기',
    startPrice: '시작가'
  };
};

// Props 인터페이스 정의
interface RecommendationSectionProps {
  onLikeClick: (e: React.MouseEvent, lotId: number, currentLikedUserIndexes?: number[]) => Promise<void>;
  favoriteManager: FavoriteManager;
  onLoadingChange?: (isLoading: boolean) => void;
}

const RecommendationSection: React.FC<RecommendationSectionProps> = ({ 
  onLikeClick, 
  favoriteManager,
  onLoadingChange
}) => {
  const { lang } = useLang();
  const { user } = useUserAuth();
  const navigate = useNavigate();
  const texts = getTexts(lang);
  const [recommendedLots, setRecommendedLots] = useState<any[]>([]); // 임시로 any 타입 사용
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 사용자가 해당 LOT을 좋아요했는지 확인하는 함수
  const isLikedByUser = (likedUserIndexes: number[] | null) => {
    if (!user?.index || !likedUserIndexes || !Array.isArray(likedUserIndexes)) {
      return false;
    }
    return likedUserIndexes.includes(user.index);
  };

  // 카드 클릭 핸들러 (ListSection과 동일한 로직)
  const handleCardClick = (lotItem: any) => {
    if (lotItem && lotItem.auction?.index) {
      // 해당 경매의 다른 아이템들을 구성 (recommendedLots에서 같은 경매의 아이템들 찾기)
      const relatedItems = recommendedLots.filter(item => 
        item.auction?.index === lotItem.auction?.index
      );
      
      // API 데이터를 ListSection의 AuctionItem 형태로 변환
      const convertedItems = relatedItems.map(item => ({
        id: item.index,
        imageUrl: item.images?.[0] ? getProfileImageSrc(item.images[0]) : '',
        auctionHouse: item.auctioneer?.name || 'BidSpace',
        auction: item.auction?.name || item.name,
        lotName: item.name,
        startingPrice: item.startPrice ? item.startPrice.toLocaleString() : '0',
        startingPriceUsd: item.startPriceUsd,
        exchangeRate: item.exchangeRate,
        liveTime: item.auction?.auctionStartAt,
        liveEndTime: item.auction?.auctionEndAt,
        isLive: new Date() >= new Date(item.auction?.auctionStartAt || '') && new Date() <= new Date(item.auction?.auctionEndAt || ''),
        photoSubmissionDate: '',
        auctionStartDate: '',
        auctionEndDate: '',
        isFavorite: item.likeCount > 0,
        isEnded: new Date() > new Date(item.auction?.auctionEndAt || ''),
        likedUserIndexes: item.likedUserIndexes || [],
        apiData: item
      }));
      
      // navigate로 lot detail 페이지로 이동 - auction ID 사용
      // selectedLotId를 추가로 전달하여 lot-detail에서 해당 lot으로 스크롤할 수 있도록 함
      navigate(`/lot-detail/${lotItem.auction.index}`, { 
        state: { 
          auction: lotItem.auction?.name || lotItem.name,
          auctionHouse: lotItem.auctioneer?.name || 'BidSpace',
          items: convertedItems,
          exchangeRate: lotItem.exchangeRate || 1300,
          selectedItem: convertedItems.find(item => item.id === lotItem.index),
          selectedLotId: lotItem.index // 선택된 lot ID 추가
        } 
      });
    }
  };

  // 즐겨찾기 업데이트 이벤트 핸들러
  useEffect(() => {
    const cleanup = favoriteManager.onFavoriteUpdate((event) => {
      setRecommendedLots(prev => 
        prev.map(lot => 
          lot.index === event.lotId
            ? { ...lot, likedUserIndexes: event.likedUserIndexes }
            : lot
        )
      );
    });

    return cleanup; // cleanup 함수 반환
  }, [favoriteManager]);

  // 추천 LOT 데이터를 가져오는 함수
  const fetchRecommendedLots = useCallback(async () => {
    try {
      setLoading(true);
      onLoadingChange?.(true);
      setError(null);
      const response = await getRecommendedLots();
      setRecommendedLots((response.data as any).data || []);
    } catch (error) {
      devError('추천 LOT 조회 실패:', error);
      setError('추천 LOT을 불러오는데 실패했습니다.');
      setRecommendedLots([]);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [onLoadingChange]);

  useEffect(() => {
    fetchRecommendedLots();
  }, [fetchRecommendedLots]);

  // 로딩 중이거나 데이터가 없거나 에러가 발생한 경우 아무것도 표시하지 않음
  if (loading || error || !recommendedLots || recommendedLots.length === 0) {
    return null;
  }

  // 모바일 레이아웃 (세로 배치)
  const MobileLayout = (
    <SectionContainer>
      <MobileTitleContainer>
        <SectionTitle>{texts.title}</SectionTitle>
        <SectionSubtitle>{texts.subtitle}</SectionSubtitle>
      </MobileTitleContainer>
      <RecommendationGrid>
        <Swiper
          modules={(recommendedLots && recommendedLots.length > 1) ? [Pagination, Autoplay] : [Pagination]}
          spaceBetween={20}
          slidesPerView={1}
          pagination={{ clickable: true }}
          autoplay={(recommendedLots && recommendedLots.length > 1) ? {
            delay: 3000,
            disableOnInteraction: false,
          } : false}
          watchOverflow={true}
          style={{ overflow: 'visible' }}
        >
          {recommendedLots?.map((item, index) => (
            <SwiperSlide key={`mobile-recommendation-${item.index}-${index}`}>
              <MobileSlideCard onClick={() => handleCardClick(item)}>
                <CardImageContainer>
                  <ImageView
                    imageUrl={getProfileImageSrc(item.images?.[0])}
                    width="calc(100% - 32px)"
                    height="calc(100% - 32px)"
                    borderRadius={0}
                    alt={item.name}
                    objectFit="contain"
                  />
                  
                  {/* Recommendation Icon - 사용자 좋아요 상태에 따른 FavoriteIcon */}
                  <FavoriteIcon 
                    isFavorite={isLikedByUser(item.likedUserIndexes)} 
                    onClick={(e) => onLikeClick(e, item.index, item.likedUserIndexes)}
                  />
                </CardImageContainer>
                <CardContent>
                  <AuctionHouse 
                    auctioneerName={item.auctioneer?.name || 'BidSpace'}
                    auctioneerIndex={item.auctioneer?.index || 0}
                  />
                  <AuctionTitle>{item.auction?.title || item.name}</AuctionTitle>
                  <LotName>{item.code || `LOT.${item.index}`}</LotName>
                </CardContent>
              </MobileSlideCard>
            </SwiperSlide>
          ))}
        </Swiper>
      </RecommendationGrid>
    </SectionContainer>
  );

  // 데스크톱 레이아웃 (가로 배치)
  const DesktopLayout = (
    <SectionContainer>
      <DesktopTitleContainer>
        <SectionTitle>{texts.title}</SectionTitle>
        <SectionSubtitle>{texts.subtitle}</SectionSubtitle>
      </DesktopTitleContainer>
      <DesktopRecommendationGrid>
        <Swiper
          modules={(recommendedLots && recommendedLots.length > 1) ? [Pagination, Navigation, Autoplay] : [Pagination, Navigation]}
          spaceBetween={20}
          slidesPerView={1}
          pagination={{ clickable: true }}
          navigation={true}
          autoplay={(recommendedLots && recommendedLots.length > 1) ? {
            delay: 3000,
            disableOnInteraction: false,
          } : false}
          watchOverflow={true}
          style={{ overflow: 'visible' }}
        >
          {recommendedLots?.map((item, index) => (
            <SwiperSlide key={`desktop-recommendation-${item.index}-${index}`}>
              <DesktopSlideCard onClick={() => handleCardClick(item)}>
                <DesktopCardImageContainer>
                  <ImageView
                    imageUrl={getProfileImageSrc(item.images?.[0])}
                    width="468px"
                    height="468px"
                    borderRadius={0}
                    alt={item.name}
                    objectFit="contain"
                  />
                  
                  {/* Recommendation Icon - 사용자 좋아요 상태에 따른 FavoriteIcon */}
                  <FavoriteIcon 
                    isFavorite={isLikedByUser(item.likedUserIndexes)} 
                    onClick={(e) => onLikeClick(e, item.index, item.likedUserIndexes)}
                  />
                </DesktopCardImageContainer>
                <DesktopCardContent>
                  <DesktopTextContent>
                    <AuctionHouse 
                      auctioneerName={item.auctioneer?.name || 'BidSpace'}
                      auctioneerIndex={item.auctioneer?.index || 0}
                    />
                    <AuctionTitle>{item.auction?.title || item.name}</AuctionTitle>
                    <LotName>{item.code || `LOT.${item.index}`}</LotName>
                  </DesktopTextContent>
                </DesktopCardContent>
              </DesktopSlideCard>
            </SwiperSlide>
          ))}
        </Swiper>
      </DesktopRecommendationGrid>
    </SectionContainer>
  );

  return (
    <ResponsiveView
      mobileView={MobileLayout}
      desktopView={DesktopLayout}
    />
  );
};

export default RecommendationSection;
